import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import HoverTooltip from '@/components/tooltip/HoverTooltip.jsx';

import './Button.css';

/**
 * Button.
 *
 * Four independent axes, all of which compose:
 *
 *   variant   primary | secondary | tertiary | subtle | link      — shape and weight
 *   tone      primary | neutral (default; subtle defaults to primary) — secondary/tertiary/subtle ONLY
 *   status    info (default) | success | warning | danger          — semantic colour
 *   size      small | medium | large                               — type scale
 *
 * plus padding (equal | wide), isFullWidth + align, and an overflow strategy.
 *
 * `icon` is the icon SLOT, `children` the LABEL — folding an icon into children breaks both.
 *
 * Colour is NOT written per variant × status pair. Every variant references one
 * "accent trio" of custom properties (--acc-fill / --acc-ink / --acc-soft …) and
 * `status` and `tone` do nothing but remap that trio. That is why every variant ×
 * status combination works — the old file wrote the pairs out by hand and had
 * holes (subtle, link and split had no status rules at all).
 *
 * `status="info"` is the default brand look (indigo --primary), not the --info hue.
 *
 * Filled status buttons ground on the -ink token, not the fill: theme.css states
 * the status fills are tint-and-dot colours, and white on --success is 2.28:1.
 * Since -ink flips between themes, the label colour flips with it via
 * --btn-on-status. See the block at the top of Button.css.
 */

const cx = (...parts) => parts.filter(Boolean).join(' ');

/** Elements whose own Enter handling must win over `isEnterTriggered`. */
const ENTER_OWNERS = new Set(['TEXTAREA', 'BUTTON', 'A', 'SELECT']);

/** Only these variants let `tone` remap the accent trio; primary/link ignore it. */
const TONED_VARIANTS = new Set(['secondary', 'tertiary', 'subtle']);

/** A toned variant's tone when none is passed: subtle has always been brand-coloured, so it keeps that. */
const DEFAULT_TONE = { subtle: 'primary' };

const Button = forwardRef(({
    variant = 'primary',
    tone,
    status = 'info',
    size = 'medium',
    padding = 'equal',
    isFullWidth = false,
    align = 'center',
    overflow = 'ellipsis',
    icon,
    iconPosition = 'left',
    /**
     * A small badge pinned to the button's top-right corner, outside the label flow.
     *
     * For marking what the action IS rather than what it does — today, that it leaves for
     * somewhere else. It sits in the corner instead of beside the label so the leading `icon`
     * can stay the action's own affordance (a plus for create, say) without the two competing
     * for the same slot. Purely decorative: it is hidden from assistive tech, so say the same
     * thing in the label or a title.
     */
    cornerIcon,
    loading = false,
    disabled = false,
    isEnterTriggered = false,
    type = 'button',
    onClick,
    className = '',
    /**
     * The button's hover explanation — why it is disabled, what it will do, what it costs.
     *
     * Named `title` because that is what every caller already passes, but it is NOT forwarded to
     * the DOM: left on the element the browser draws its own tooltip, so a surface that uses
     * HoverTooltip anywhere else ended up with two unrelated-looking tooltips depending on which
     * part of a row you crossed. It goes through the same HoverTooltip the truncated-label case
     * below already uses, and it wins over that one — an explanation is worth more than a repeat
     * of a label you can half-see.
     */
    title,
    children,
    ...props
}, forwardedRef) => {
    const innerRef = useRef(null);
    const labelRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const hasLabel = children !== undefined && children !== null
        && children !== false && children !== '';
    const isIconOnly = Boolean(icon) && !hasLabel;

    const setRefs = useCallback((node) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
    }, [forwardedRef]);

    /**
     * Enter-to-click, for the single confirm button of a form.
     *
     * Guards, all of which the previous version was missing: held keys don't
     * repeat-fire, an Enter another handler already consumed is left alone, and
     * Enter inside a textarea / on another button or link does that element's job
     * instead of this one's. Two buttons mounted with this prop will still both
     * fire — that is inherent to a document-level listener.
     */
    useEffect(() => {
        if (!isEnterTriggered || disabled || loading) return undefined;

        const handleKeyDown = (e) => {
            if (e.key !== 'Enter' || e.repeat || e.defaultPrevented) return;
            const target = e.target;
            if (target instanceof HTMLElement
                && (ENTER_OWNERS.has(target.tagName) || target.isContentEditable)) return;
            e.preventDefault();
            innerRef.current?.click();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isEnterTriggered, disabled, loading]);

    /**
     * Responsiveness, option 2 (the default): the label ellipsises and the full
     * text is available on hover. The tooltip is only armed when the label is
     * actually clipped, so an auto-width button never grows a spurious tooltip.
     */
    const wantsTooltip = overflow === 'ellipsis' && typeof children === 'string' && children.length > 0;

    useEffect(() => {
        if (!wantsTooltip) {
            setIsTruncated(false);
            return undefined;
        }
        const el = labelRef.current;
        if (!el) return undefined;

        const check = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
        check();
        const observer = new ResizeObserver(check);
        observer.observe(el);
        return () => observer.disconnect();
    }, [wantsTooltip, children]);

    const handleClick = (e) => {
        if (disabled || loading) return;
        onClick?.(e);
    };

    const classes = cx(
        'btn',
        `btn--${variant}`,
        `btn--${size}`,
        `btn--pad-${padding}`,
        (tone ?? DEFAULT_TONE[variant] ?? 'neutral') === 'neutral' && TONED_VARIANTS.has(variant) && 'btn--neutral',
        status !== 'info' && `btn--${status}`,
        isFullWidth && 'btn--full',
        isFullWidth && `btn--align-${align}`,
        overflow === 'wrap' && 'btn--wrap',
        isIconOnly && 'btn--icon-only',
        cornerIcon && 'btn--cornered',
        loading && 'is-loading',
        className,
    );

    /**
     * Loading swaps the ICON for the spinner and leaves the label alone. With no
     * icon to take over, the spinner is inserted before the label instead. Either
     * way the text stays readable — the button says what it is doing while it does
     * it, rather than going blank.
     */
    const spinner = <FontAwesomeIcon icon={faSpinner} spin className="btn-spinner" />;
    // A FontAwesome definition, or an element boxed so .btn-icon--node can size it.
    const iconEl = !icon ? null
        : React.isValidElement(icon)
            ? <span className="btn-icon btn-icon--node">{icon}</span>
            : <FontAwesomeIcon icon={icon} className="btn-icon" />;

    let lead = iconPosition === 'left' ? iconEl : null;
    let trail = iconPosition === 'right' ? iconEl : null;
    if (loading) {
        // A right-hand icon keeps its side; everything else spins on the left.
        if (icon && iconPosition === 'right') trail = spinner;
        else lead = spinner;
    }

    const button = (
        <button
            ref={setRefs}
            type={type}
            className={classes}
            onClick={handleClick}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...props}
        >
            {lead}
            {hasLabel && <span ref={labelRef} className="btn-label">{children}</span>}
            {trail}
            {cornerIcon && (
                <span className="btn-corner" aria-hidden="true">
                    {React.isValidElement(cornerIcon)
                        ? cornerIcon
                        : <FontAwesomeIcon icon={cornerIcon} />}
                </span>
            )}
        </button>
    );

    /* A labelled button is wrapped whether or not the tooltip is armed, exactly as before: the
       anchor is part of its box, and adding or dropping it as the label crosses its own width would
       shift the layout on every resize. A `title` only ADDS the wrapper to buttons that had none —
       icon-only ones — and nothing changes for a button that passes neither. */
    if (!wantsTooltip && !title) return button;

    return (
        <HoverTooltip
            className={cx('btn-tip-anchor', isFullWidth && 'btn-tip-anchor--full')}
            panelClassName="btn-tip"
            content={title || children}
            disabled={!title && !isTruncated}
        >
            {button}
        </HoverTooltip>
    );
});

Button.displayName = 'Button';

/* ── Variant exports ──────────────────────────────────────────────────────────
   `variant` is set AFTER the spread on purpose: a PrimaryButton stays primary
   even if a call site passes variant. Every other prop still passes through, so
   `<SecondaryButton tone="neutral" status="danger" size="small" />` works. */

const pinVariant = (variant, displayName) => {
    const Pinned = forwardRef((props, ref) => <Button {...props} ref={ref} variant={variant} />);
    Pinned.displayName = displayName;
    return Pinned;
};

export const PrimaryButton = pinVariant('primary', 'PrimaryButton');
export const SecondaryButton = pinVariant('secondary', 'SecondaryButton');
export const TertiaryButton = pinVariant('tertiary', 'TertiaryButton');
export const SubtleButton = pinVariant('subtle', 'SubtleButton');
export const LinkButton = pinVariant('link', 'LinkButton');

/* ── Split button ─────────────────────────────────────────────────────────── */

/**
 * The menu half of a SplitButton, portaled to <body> so it is never clipped by
 * an ancestor's overflow. Positioned fixed against the side button's rect and
 * re-placed on scroll/resize — the same approach HoverTooltip uses.
 */
const SplitMenu = ({ anchorRef, items, onClose }) => {
    const menuRef = useRef(null);
    const [pos, setPos] = useState(null);

    useLayoutEffect(() => {
        const place = () => {
            const anchor = anchorRef.current;
            if (!anchor) return;
            const r = anchor.getBoundingClientRect();
            const menu = menuRef.current;
            const w = menu?.offsetWidth || 0;
            const h = menu?.offsetHeight || 0;

            // Right-aligned to the trigger, then clamped into the viewport.
            const left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8));
            const spaceBelow = window.innerHeight - r.bottom;
            const openUp = h > 0 && spaceBelow < h + 8 && r.top > spaceBelow;

            setPos(openUp
                ? { left, bottom: window.innerHeight - r.top + 4 }
                : { left, top: r.bottom + 4 });
        };

        place();
        // offsetWidth/Height are 0 before the menu paints; one rAF corrects the
        // right-alignment and the flip decision once it has measured.
        const raf = requestAnimationFrame(place);
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [anchorRef]);

    useEffect(() => {
        const handleDown = (e) => {
            if (menuRef.current?.contains(e.target)) return;
            // Clicks on the trigger fall through to its own onClick, which toggles.
            if (anchorRef.current?.contains(e.target)) return;
            onClose();
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') {
                onClose();
                anchorRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', handleDown);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleDown);
            document.removeEventListener('keydown', handleKey);
        };
    }, [anchorRef, onClose]);

    return createPortal(
        <div
            ref={menuRef}
            role="menu"
            className="btn-menu"
            style={{
                position: 'fixed',
                left: pos?.left ?? 0,
                top: pos?.top,
                bottom: pos?.bottom,
                visibility: pos ? 'visible' : 'hidden',
            }}
        >
            {/* Same reasoning as the button above: an item's `title` is usually why it is disabled,
                and it reads through HoverTooltip rather than the browser's own. */}
            {items.map((item, i) => (
                <HoverTooltip
                    key={item.key ?? item.label ?? i}
                    className="btn-menu-item-tip"
                    content={item.title}
                >
                    <button
                        type="button"
                        role="menuitem"
                        className={cx('btn-menu-item', item.destructive && 'btn-menu-item--danger')}
                        disabled={item.disabled}
                        onClick={(e) => {
                            onClose();
                            item.onClick?.(e);
                        }}
                    >
                        {item.icon && <FontAwesomeIcon icon={item.icon} className="btn-menu-icon" />}
                        <span className="btn-menu-label">{item.label}</span>
                    </button>
                </HoverTooltip>
            ))}
        </div>,
        document.body,
    );
};

/**
 * Two real buttons, joined but visually separate — a 2px gap lets the page ground
 * through between them, which is what the old single-button-with-a-divider version
 * was missing.
 *
 * The left segment runs `onClick`. The right segment does ONE of two things:
 *   · `menuItems` given  → opens a portaled dropdown; each item is {label, icon,
 *                          onClick, disabled?, destructive?, title?, key?}
 *   · `onSideClick` given → a plain second action button
 *
 * Both halves take the same variant/tone/status/size so the pair reads as one
 * control. `sideLabel` turns the right segment into a labelled button; without it
 * the side is icon-only (chevron by default).
 */
export const SplitButton = ({
    children,
    icon,
    onClick,
    sideIcon = faChevronDown,
    sideLabel,
    onSideClick,
    menuItems,
    variant = 'primary',
    tone = 'primary',
    status = 'info',
    size = 'medium',
    padding = 'equal',
    disabled = false,
    loading = false,
    sideDisabled = false,
    // Stretches the WRAPPER; the main half takes the slack and the side stays square.
    isFullWidth = false,
    className = '',
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const sideRef = useRef(null);
    const hasMenu = Array.isArray(menuItems) && menuItems.length > 0;

    const close = useCallback(() => setIsOpen(false), []);

    const handleSideClick = (e) => {
        if (hasMenu) {
            setIsOpen((open) => !open);
            return;
        }
        onSideClick?.(e);
    };

    const shared = { variant, tone, status, size };

    return (
        <div className={cx('btn-split', isFullWidth && 'btn-split--full', className)} {...props}>
            <Button
                {...shared}
                padding={padding}
                icon={icon}
                onClick={onClick}
                disabled={disabled}
                loading={loading}
                className="btn-split-main"
            >
                {children}
            </Button>

            <Button
                {...shared}
                ref={sideRef}
                padding="equal"
                icon={sideIcon}
                iconPosition="right"
                onClick={handleSideClick}
                disabled={disabled || sideDisabled}
                className="btn-split-side"
                aria-haspopup={hasMenu ? 'menu' : undefined}
                aria-expanded={hasMenu ? isOpen : undefined}
            >
                {sideLabel}
            </Button>

            {hasMenu && isOpen && (
                <SplitMenu anchorRef={sideRef} items={menuItems} onClose={close} />
            )}
        </div>
    );
};

export default Button;
